/**
 * Composable usePopover
 *
 * Estado e utilitários para popovers/dropdowns/menus que abrem ancorados a um trigger.
 *
 * Resolve dois problemas comuns em layouts responsivos:
 *
 * 1. Múltiplas instâncias por breakpoint (ex: o mesmo botão renderizado dentro
 *    de um wrapper `hidden sm:flex` e outro `sm:hidden`). Passando uma `key`,
 *    o estado de abertura é compartilhado entre as instâncias — abrir uma abre
 *    a outra, e o estado persiste ao mudar o breakpoint.
 *
 * 2. Click-outside que se confunde quando há mais de uma instância no DOM.
 *    O helper `useClickOutside` ignora instâncias escondidas por `display:none`
 *    em ancestrais (típico de classes responsivas como `sm:hidden`), de modo
 *    que apenas a instância visível decide fechar.
 */
import { ref, onMounted, onUnmounted } from 'vue'

const sharedStates = new Map()

/**
 * @param {string} [key] - Quando fornecido, o estado é compartilhado entre todas
 *   as chamadas com a mesma chave. Sem chave, o estado é local à instância.
 * @returns {{
 *   isOpen: import('vue').Ref<boolean>,
 *   toggle: () => void,
 *   open: () => void,
 *   close: () => void,
 * }}
 */
export function usePopover(key) {
  let isOpen
  if (key) {
    if (!sharedStates.has(key)) {
      sharedStates.set(key, ref(false))
    }
    isOpen = sharedStates.get(key)
  } else {
    isOpen = ref(false)
  }

  return {
    isOpen,
    toggle: () => (isOpen.value = !isOpen.value),
    open: () => (isOpen.value = true),
    close: () => (isOpen.value = false),
  }
}

/**
 * Verifica se o elemento está efetivamente renderizado.
 * Funciona com `position: fixed` mesmo dentro de ancestrais `display:none`
 * (que zeram o layout do subtree inteiro).
 *
 * @param {Element | null | undefined} el
 * @returns {boolean}
 */
export function isElementVisible(el) {
  if (!el) return false
  if (typeof el.checkVisibility === 'function') return el.checkVisibility()
  return el.getClientRects().length > 0
}

/**
 * Listener de click-outside que ignora instâncias escondidas por CSS de ancestrais.
 *
 * Aceita um ref único ou um array de refs — útil para popovers cujo trigger e
 * conteúdo estão em containers separados (ex: trigger ancorado e conteúdo em
 * Teleport). Se nenhuma das instâncias estiver visível, o callback não dispara.
 *
 * @param {import('vue').Ref<Element | null> | Array<import('vue').Ref<Element | null>>} targets
 * @param {(event: MouseEvent) => void} onOutside
 */
export function useClickOutside(targets, onOutside) {
  const refs = Array.isArray(targets) ? targets : [targets]

  const handler = event => {
    let anyVisible = false
    for (const r of refs) {
      const el = r.value
      if (!isElementVisible(el)) continue
      anyVisible = true
      if (el.contains(event.target)) return
    }
    if (anyVisible) onOutside(event)
  }

  onMounted(() => document.addEventListener('click', handler))
  onUnmounted(() => document.removeEventListener('click', handler))
}
